import { PropsWithChildren, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { faTh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CTOTitle as Title,
  RiskMatrixModal,
  translate,
  RiskLevelEnum,
  residualRiskLevel,
} from "@shape/reef-shared";
import { SelectField, TextAreaField } from "components/Form";
import { Row } from "ui/styled";
import { CreateStep2Body } from "types";
import { Columns, MatrixButton, Section } from "../styled";
import { SeeMatrixSecondaryButton } from "./styled";

interface RiskLevelWithMatrixAndCommentProps {
  children?: JSX.Element;
  commentDefaultValue?: string | null;
  fieldName: "risk_level" | "risk_level_after_measures";
  commentFieldName: "risk_level_justification" | "risk_level_after_measures_comment";
  form: UseFormReturn<CreateStep2Body>;
  readOnly: boolean;
  riskLevelDefaultValue: RiskLevelEnum | null;
  sectionTitle: string;
  selectKey: string;
  selectLabel: string;
  withComment: boolean;
  customStyle?: React.CSSProperties;
}

export function RiskLevelWithMatrixAndComment({
  children,
  commentDefaultValue,
  fieldName,
  form,
  readOnly,
  riskLevelDefaultValue,
  sectionTitle,
  selectKey,
  selectLabel,
  withComment,
  commentFieldName,
  customStyle,
}: PropsWithChildren<RiskLevelWithMatrixAndCommentProps>) {
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);

  return (
    <Section style={customStyle}>
      <Title isUpperCase>{sectionTitle}</Title>

      <Columns>
        <div style={{ width: "300px" }}>
          <SelectField
            key={selectKey}
            form={form}
            label={selectLabel}
            name={fieldName}
            options={residualRiskLevel()}
            rules={{ required: true }}
            placeholder={translate("formGeneric.select")}
            readOnly={readOnly}
            defaultValue={riskLevelDefaultValue}
            onFilterChange={() => null}
          />
        </div>

        <MatrixButton>
          <SeeMatrixSecondaryButton
            type="button"
            borderWidth={2}
            onClick={() => setIsRiskMatrixModalOpen(true)}
          >
            <FontAwesomeIcon icon={faTh} /> {translate("ctoPage.riskMatrix.seeRiskMatrix")}
          </SeeMatrixSecondaryButton>
        </MatrixButton>
      </Columns>

      <RiskMatrixModal
        isOpen={isRiskMatrixModalOpen}
        onClose={() => setIsRiskMatrixModalOpen(false)}
        setRiskLevel={(value: RiskLevelEnum | "") => {
          form.setValue(fieldName, value === "" ? null : value);
        }}
        readOnly={readOnly}
      />

      {children}

      {withComment && (
        <Row marginBottom={25}>
          <TextAreaField
            form={form}
            name={commentFieldName}
            readOnly={readOnly}
            defaultValue={commentDefaultValue}
            label={
              commentFieldName === "risk_level_after_measures_comment"
                ? translate("generic.commentWithField", {
                    field: translate("generic.optional"),
                  })
                : translate("generic.justificationWithField", {
                    field: translate("generic.optional"),
                  })
            }
            placeholder={translate("generic.typeHere")}
          />
        </Row>
      )}
    </Section>
  );
}
